import { useTranslation } from 'react-i18next'
import { useUser } from '../../../../contexts/userContext'
import { SmallModal } from '../../../../common/components/Modal/SmallModal'
import { InputField } from '../../../../common/components/input/InputField'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Button } from '../../../../common/components/button/Button'
import { Tag } from '../../../../types/commonTypes'
import { createBusinessTag, deleteBusinessTag, editBusinessTag } from '../../../../api/userApiService'
import { CellElement, Table, TableRow } from '../../../reports/components/GenericTable'
import shaderStyles from './PlayerAccountTable.module.css'
import styles from './ManageTagsModal.module.css'
import classNames from 'classnames'
import { getIcon } from '../../../../common/components/icons/utils'
import { ThreeDotsWithOverlay } from '../../../../common/components/loaders/ThreeDotsWithOverlay/ThreeDotsWithOverlay'
import { useSortedTable } from '../../../../hooks/useSortedTable'
import { ReportSortDir } from '../../../../contexts/ReportsContextProvider'

type ManageTagsModalProps = {
  onClose: () => void
}

export const SAIGE_BUTTON_ID = 'SAIGE_BUTTON_ID'

export const ManageTagsModal: React.FC<ManageTagsModalProps> = ({ onClose }) => {
  const { t } = useTranslation()
  const { user, refreshUser } = useUser()
  const [inputValue, setInputValue] = useState('')
  const [isEditing, setIsEditing] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const {
    sortedItems: sortedTags,
    sortState,
    setSort,
  } = useSortedTable<Tag>({
    items: user?.tags || [],
    initialSort: { sortBy: 'label', direction: ReportSortDir.ASC },
    getSortableValue: (member, key) => {
      const value = member[key]

      if (typeof value === 'string') return value.trim().toLocaleLowerCase()
      if (typeof value === 'number' || typeof value === 'boolean') return value

      // Fallback for undefined/null or unexpected types
      return ''
    },
  })

  const getAllTags = useCallback(async () => {
    setIsLoading(true)
    refreshUser()
    setIsLoading(false)
  }, [refreshUser])

  useEffect(() => {
    getAllTags()
  }, [getAllTags])

  const handleOnChange = (e: React.SyntheticEvent<HTMLInputElement>) => {
    setInputValue(e.currentTarget.value)
  }

  const handleDeleteTag = useCallback(
    async (tag: Tag) => {
      setIsLoading(true)
      await deleteBusinessTag({ tag })
      getAllTags()
      setIsLoading(false)
    },
    [getAllTags],
  )

  const handleEditTag = useCallback(async () => {
    setIsLoading(true)
    if (isEditing) {
      await editBusinessTag({ tagId: isEditing, newText: inputValue })
      getAllTags()
      setInputValue('')
      setIsEditing(null)
    }
    setIsLoading(false)
  }, [getAllTags, inputValue, isEditing])

  const handleCreateTag = useCallback(async () => {
    setIsLoading(true)
    await createBusinessTag({ newTag: inputValue })
    getAllTags()
    setInputValue('')
    setIsLoading(false)
  }, [getAllTags, inputValue])

  const setTagToEdit = useCallback((tag: Tag) => {
    setInputValue(tag.label)
    setIsEditing(tag.id)
  }, [])

  const cancelTagToEdit = useCallback(() => {
    setInputValue('')
    setIsEditing(null)
  }, [])

  const createActionCell = useCallback(
    (tag: Tag) => {
      return (
        <div className={classNames(shaderStyles.cell, shaderStyles.playerAccountActionsContainer)}>
          <button onClick={() => setTagToEdit(tag)}>
            <span>{getIcon('text')}</span>
            <span>{t('settings_organization.players.tags.manage.button.edit', 'Edit')}</span>
          </button>
          <button onClick={() => handleDeleteTag(tag)}>
            <span>{getIcon('trash')}</span>
            <span>{t('settings_organization.players.tags.manage.button.delete', 'Delete')}</span>
          </button>
        </div>
      )
    },
    [handleDeleteTag, setTagToEdit, t],
  )

  const tableRowData = useMemo((): TableRow[] => {
    const rows: TableRow[] = []
    sortedTags.forEach((tag) => {
      const row = tag as Tag
      const tableRow: CellElement[] = [<div className={styles.tagCell}>{tag.label}</div>, createActionCell(row)]
      rows.push(tableRow)
    })
    return rows
  }, [createActionCell, sortedTags])

  return (
    <>
      <SmallModal
        onClose={onClose}
        headerText={t('settings_organization.players.tags.manage.header', 'Manage tags')}
        sendButtonText={t('settings_organization.players.tags.manage.button.close', 'Close')}
        iconName='tag'
        sendButtonIsSubmit={false}
        hideCancelButton
        onSubmit={onClose}
      >
        {isLoading && <ThreeDotsWithOverlay />}
        <div className={styles.contentContainer}>
          <div className={styles.inputHeaderContainer}>
            <div className={classNames(styles.inputHeader, isEditing ? styles.inputEditShow : styles.inputEditHide)}>
              {t('settings_organization.players.tags.manage.edit_tag', 'Edit tag')}
            </div>
            <div
              className={classNames(styles.inputHeader, isEditing ? styles.inputCreateHide : styles.inputCreateShow)}
            >
              {t('settings_organization.players.tags.manage.create_new', 'Create new tag')}
            </div>
          </div>
          <div className={styles.inputContainer}>
            <InputField
              value={inputValue}
              onChange={handleOnChange}
              placeholder={t('settings_organization.players.tags.manage.input.placeholder', 'Type here')}
              showCharsCount
              maxLength={40}
              className={styles.input}
              inputContainerClassName={styles.forwardInputContainer}
            />
            <Button
              variant='tiny'
              onClick={isEditing ? handleEditTag : handleCreateTag}
              disabled={inputValue.length < 2}
            >
              {t('settings_organization.players.tags.buttons.save', 'Save')}
            </Button>
            <Button
              variant='outline-tiny'
              onClick={cancelTagToEdit}
              className={classNames(styles.cancelButton, isEditing ? styles.cancelButtonShow : styles.cancelButtonHide)}
            >
              {t('settings_organization.players.tags.buttons.cancel', 'Cancel')}
            </Button>
          </div>
          <Table
            headerWidths={['100%', 'fit-content']}
            headerLabels={[
              t('settings_organization.players.tags.manage.table.header.tag', 'Tag'),
              t('settings_organization.players.tags.manage.table.header.actions', 'Actions'),
            ]}
            rows={tableRowData}
            loading={isLoading}
            customClassName={styles.table}
            removeHorizontalMargins={false}
            isSortable
            headerSortTags={['label']}
            onSort={(sortKey: string) => (direction: string) => {
              setSort(sortKey as keyof Tag, direction as ReportSortDir)
            }}
            activeSortCol={sortState.sortBy}
            noSortIndexes={[false, true]}
            tinyTable={true}
          />
        </div>
      </SmallModal>
    </>
  )
}
